import React, {
  useEffect,
  useState,
  useCallback,
  useRef,
  useLayoutEffect,
  useId,
} from "react";
import { createPortal } from "react-dom";
import {
  getAiInsights,
  regenerateAiInsights,
  recordAiInsightInteraction,
  rateAiInsight,
  saveAiInsightDisplayOrder,
} from "../../services/aiInsights";
import styles from "./aiInsightsBlock.module.css";
import InstitutionRevenueHistoryCard from "./institutionRevenueHistoryCard";
import InstitutionBillingHistoryCard from "./institutionBillingHistoryCard";
import InstitutionAverageTicketHistoryCard from "./institutionAverageTicketHistoryCard";

const TYPE_LABELS = {
  revenue: "Receita",
  delinquency: "Inadimplência",
  average_ticket: "Ticket médio",
  dropout: "Evasão",
  finance_prediction: "Predição (inadimplência)",
};

const TIME_LABELS = {
  historical: "Histórico",
  forecast: "Projeção futura",
  mixed: "Histórico + Projeção",
};

const formatDate = (raw) => {
  if (!raw) return "-";
  try {
    const dt = new Date(raw);
    return dt.toLocaleDateString("pt-BR");
  } catch {
    return raw;
  }
};

const formatDateTime = (raw) => {
  if (!raw) return "-";
  try {
    const dt = new Date(raw);
    return `${dt.toLocaleDateString("pt-BR")} ${dt.toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" })}`;
  } catch {
    return raw;
  }
};

const splitEvidenceItem = (raw) => {
  if (typeof raw !== "string") return { label: null, value: String(raw ?? "") };
  const trimmed = raw.trim();
  const sepIdx = trimmed.search(/[:=]\s/);
  if (sepIdx <= 0) return { label: null, value: trimmed };
  const label = trimmed.slice(0, sepIdx).trim();
  const value = trimmed.slice(sepIdx + 1).replace(/^[\s:=]+/, "").trim();
  if (!label || !value || label.length > 60) return { label: null, value: trimmed };
  return { label, value };
};

// Tokens com destaque visual no texto livre (summary, evidence value, rangeWarning).
// Reconhece padrões já garantidos pelo prompt + AiInsightTextNormalizer no backend:
//   - Moeda completa: R$ 1.234,56 / R$ 2.258.514,20
//   - Moeda abreviada: R$ 299 mil / R$ 13,9 mil / R$ 2,26 milhões / R$ 1 milhão
//   - Percentual: 12,5% / 87% / 9,02%
//   - Mês/Ano: Jan/2026 / Junho/2025
//   - Intervalo de meses: Jun-Jul/2025 / Jan-Fev
const HIGHLIGHT_TOKEN_REGEX = new RegExp(
  [
    String.raw`R\$\s*\d{1,3}(?:\.\d{3})*(?:,\d+)?(?:\s+(?:mil|milhão|milhões|milhao|milhoes))?`,
    String.raw`\d+(?:,\d+)?\s*%`,
    String.raw`(?:Jan|Fev|Mar|Abr|Mai|Jun|Jul|Ago|Set|Out|Nov|Dez)-(?:Jan|Fev|Mar|Abr|Mai|Jun|Jul|Ago|Set|Out|Nov|Dez)(?:/\d{2,4})?`,
    String.raw`(?:Jan|Fev|Mar|Abr|Mai|Jun|Jul|Ago|Set|Out|Nov|Dez|Janeiro|Fevereiro|Março|Marco|Abril|Maio|Junho|Julho|Agosto|Setembro|Outubro|Novembro|Dezembro)/\d{2,4}`,
  ].join("|"),
  "g"
);

const renderHighlights = (text, highlightClass) => {
  if (typeof text !== "string" || text.length === 0) return text;
  const regex = new RegExp(HIGHLIGHT_TOKEN_REGEX.source, "g");
  const parts = [];
  let lastIndex = 0;
  let match;
  while ((match = regex.exec(text)) !== null) {
    if (match.index > lastIndex) {
      parts.push(text.slice(lastIndex, match.index));
    }
    parts.push(
      <strong key={`h-${match.index}`} className={highlightClass}>
        {match[0]}
      </strong>
    );
    lastIndex = regex.lastIndex;
  }
  if (parts.length === 0) return text;
  if (lastIndex < text.length) parts.push(text.slice(lastIndex));
  return parts;
};

const EvidenceItem = ({ text, labelClass, valueClass, highlightClass }) => {
  const { label, value } = splitEvidenceItem(text);
  const valueChildren = renderHighlights(value, highlightClass);
  if (!label) return <>{valueChildren}</>;
  return (
    <>
      <span className={labelClass}>{label}:</span>
      <span className={valueClass}>{valueChildren}</span>
    </>
  );
};

// Numero maximo de evidencias renderizadas no card (visao geral). O restante fica
// disponivel no modal "Ver mais", garantindo altura previsivel sem corte vertical.
const CARD_EVIDENCE_LIMIT = 4;

const RATING_VALUES = [1, 2, 3, 4, 5];

// Componente de avaliacao por estrelas (1..5). Usado tanto no card quanto no modal.
// Emite onChange(novaNota) quando o usuario clica e onHover(valor) durante o hover
// para preview visual. value e a nota persistida; hoverValue (opcional) sobrepoe
// o preenchimento durante o hover.
const RatingStars = ({
  value,
  hoverValue,
  onChange,
  onHover,
  disabled,
  ariaLabelPrefix = "Avaliar com",
}) => {
  const display = hoverValue || value || 0;
  return (
    <span
      className={styles.ratingStars}
      role="radiogroup"
      aria-label="Avaliação do insight"
      onMouseLeave={() => onHover && onHover(0)}
    >
      {RATING_VALUES.map((star) => {
        const filled = star <= display;
        const label = `${ariaLabelPrefix} ${star} ${star === 1 ? "estrela" : "estrelas"}`;
        return (
          <button
            key={star}
            type="button"
            role="radio"
            aria-checked={value === star}
            aria-label={label}
            title={label}
            disabled={disabled}
            className={`${styles.ratingStarBtn} ${filled ? styles.ratingStarBtn_active : ""}`}
            onClick={() => !disabled && onChange && onChange(star)}
            onMouseEnter={() => !disabled && onHover && onHover(star)}
            onFocus={() => !disabled && onHover && onHover(star)}
            onBlur={() => onHover && onHover(0)}
            onKeyDown={(e) => {
              if (disabled) return;
              if (e.key === "ArrowRight" || e.key === "ArrowUp") {
                e.preventDefault();
                onChange && onChange(Math.min(5, (value || star) + 1));
              } else if (e.key === "ArrowLeft" || e.key === "ArrowDown") {
                e.preventDefault();
                onChange && onChange(Math.max(1, (value || star) - 1));
              }
            }}
          >
            {filled ? "★" : "☆"}
          </button>
        );
      })}
    </span>
  );
};

const InsightCard = ({
  insight,
  snapshotId,
  institutionId,
  onDismiss,
  initialRating = 0,
  onRated,
}) => {
  const [dismissed, setDismissed] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [contentOverflows, setContentOverflows] = useState(false);
  const [rating, setRating] = useState(initialRating || 0);
  const [hoverRating, setHoverRating] = useState(0);
  const [ratingState, setRatingState] = useState("idle");
  const [ratingError, setRatingError] = useState(null);

  const modalTitleId = useId();
  const titleRef = useRef(null);
  const summaryRef = useRef(null);
  const evidenceRef = useRef(null);

  const typeKey = insight.type || "revenue";
  const timeKey = insight.timeContext || "historical";

  const measureOverflow = useCallback(() => {
    const titleEl = titleRef.current;
    const summaryEl = summaryRef.current;
    const evidenceEl = evidenceRef.current;
    const epsilon = 2;
    let overflows = false;
    if (titleEl) overflows ||= titleEl.scrollHeight > titleEl.clientHeight + epsilon;
    if (summaryEl) overflows ||= summaryEl.scrollHeight > summaryEl.clientHeight + epsilon;
    if (evidenceEl) {
      overflows ||= evidenceEl.scrollHeight > evidenceEl.clientHeight + epsilon;
      if (!overflows) {
        for (const li of evidenceEl.querySelectorAll("li")) {
          if (li.scrollWidth > li.clientWidth + epsilon) {
            overflows = true;
            break;
          }
        }
      }
    }
    setContentOverflows(overflows);
  }, []);

  useLayoutEffect(() => {
    measureOverflow();
    const ro = typeof ResizeObserver !== "undefined" ? new ResizeObserver(measureOverflow) : null;
    [titleRef.current, summaryRef.current, evidenceRef.current].forEach((el) => {
      if (el && ro) ro.observe(el);
    });
    window.addEventListener("resize", measureOverflow);
    return () => {
      window.removeEventListener("resize", measureOverflow);
      if (ro) ro.disconnect();
    };
  }, [insight.summary, insight.title, insight.evidence, measureOverflow]);

  useEffect(() => {
    if (!modalOpen) return;
    const onKey = (e) => {
      if (e.key === "Escape") setModalOpen(false);
    };
    document.addEventListener("keydown", onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [modalOpen]);

  const handleDismiss = useCallback(() => {
    setDismissed(true);
    recordAiInsightInteraction({
      institutionId,
      snapshotId,
      insightType: insight.type,
      dismissed: true,
    });
    if (onDismiss) onDismiss(insight);
  }, [institutionId, snapshotId, insight, onDismiss]);

  // Mantém o estado local em sincronia caso o pai recarregue o snapshot e
  // mude `initialRating` (ex.: outra aba avaliou e o GET retornou nova nota).
  useEffect(() => {
    setRating(initialRating || 0);
    setRatingState("idle");
    setRatingError(null);
  }, [initialRating]);

  const handleRate = useCallback(
    async (newRating) => {
      if (!insight.id || !snapshotId || !institutionId) return;
      if (newRating === rating) return;

      const previous = rating;
      setRating(newRating);
      setRatingState("saving");
      setRatingError(null);

      try {
        await rateAiInsight({
          institutionId,
          snapshotId,
          insightId: insight.id,
          insightType: insight.type,
          rating: newRating,
        });
        setRatingState("saved");
        if (onRated) onRated(insight, newRating);
      } catch (err) {
        setRating(previous);
        setRatingState("error");
        setRatingError(err?.message || "Falha ao salvar avaliação.");
      }
    },
    [insight, snapshotId, institutionId, rating, onRated]
  );

  if (dismissed) return null;

  const allEvidence = Array.isArray(insight.evidence) ? insight.evidence : [];
  const hasEvidence = allEvidence.length > 0;
  const visibleCardEvidence = allEvidence.slice(0, CARD_EVIDENCE_LIMIT);
  const hasHiddenEvidence = allEvidence.length > visibleCardEvidence.length;

  return (
    <div className={styles.card}>
      <div className={styles.cardHeader}>
        <div className={styles.cardTypeAndBadges}>
          <span className={`${styles.cardType} ${styles[`cardType_${typeKey}`]}`}>
            {TYPE_LABELS[typeKey] || typeKey}
          </span>
          <span className={`${styles.timeBadge} ${styles[`timeBadge_${timeKey}`] || ""}`}>
            {TIME_LABELS[timeKey] || timeKey}
          </span>
        </div>
        <button
          type="button"
          className={styles.dismissBtn}
          onClick={handleDismiss}
          aria-label="Dispensar insight"
          title="Dispensar"
        >
          ×
        </button>
      </div>

      <h4 ref={titleRef} className={styles.cardTitle}>
        {insight.title}
      </h4>
      <p ref={summaryRef} className={styles.cardSummary}>
        {renderHighlights(insight.summary, styles.highlight)}
      </p>

      {hasEvidence && (
        <ul ref={evidenceRef} className={styles.evidenceList}>
          {visibleCardEvidence.map((ev, i) => (
            <li key={i}>
              <EvidenceItem
                text={ev}
                labelClass={styles.evidenceLabel}
                valueClass={styles.evidenceValue}
                highlightClass={styles.highlight}
              />
            </li>
          ))}
        </ul>
      )}

      {(contentOverflows || hasHiddenEvidence) && (
        <button
          type="button"
          className={styles.verMaisBtn}
          onClick={() => setModalOpen(true)}
        >
          {hasHiddenEvidence
            ? `Ver mais (+${allEvidence.length - visibleCardEvidence.length})`
            : "Ver mais"}
        </button>
      )}

      {insight.isExtendedRange && insight.rangeWarning && (
        <div className={styles.queryRangeWarning}>
          {renderHighlights(insight.rangeWarning, styles.highlight)}
        </div>
      )}

      {insight.periodLabel ? (
        <div className={styles.cardFooter}>
          <span className={styles.periodLabel}>{insight.periodLabel}</span>
        </div>
      ) : null}

      {insight.id && (
        <div className={styles.ratingRow}>
          <span className={styles.ratingLabel}>Avalie</span>
          <RatingStars
            value={rating}
            hoverValue={hoverRating}
            onChange={handleRate}
            onHover={setHoverRating}
            disabled={ratingState === "saving"}
          />
          {ratingState === "saving" && (
            <span className={styles.ratingFeedback}>Salvando...</span>
          )}
          {ratingState === "saved" && rating > 0 && (
            <span className={styles.ratingFeedback}>Obrigado pelo feedback!</span>
          )}
          {ratingState === "error" && (
            <span className={`${styles.ratingFeedback} ${styles.ratingFeedback_error}`}>
              {ratingError}
            </span>
          )}
        </div>
      )}

      {modalOpen &&
        createPortal(
          <div
            className={styles.modalBackdrop}
            role="presentation"
            onClick={() => setModalOpen(false)}
          >
            <div
              className={styles.modalPanel}
              role="dialog"
              aria-modal="true"
              aria-labelledby={modalTitleId}
              onClick={(e) => e.stopPropagation()}
            >
              <div className={styles.modalHeader}>
                <div className={styles.modalHeaderBadges}>
                  <span className={`${styles.cardType} ${styles[`cardType_${typeKey}`]}`}>
                    {TYPE_LABELS[typeKey] || typeKey}
                  </span>
                  <span className={`${styles.timeBadge} ${styles[`timeBadge_${timeKey}`] || ""}`}>
                    {TIME_LABELS[timeKey] || timeKey}
                  </span>
                </div>
                <button
                  type="button"
                  className={styles.modalCloseBtn}
                  onClick={() => setModalOpen(false)}
                  aria-label="Fechar"
                >
                  ×
                </button>
              </div>
              <h4 id={modalTitleId} className={styles.modalTitle}>
                {insight.title}
              </h4>
              <p className={styles.modalSummary}>
                {renderHighlights(insight.summary, styles.highlight)}
              </p>
              {hasEvidence && (
                <ul className={styles.modalEvidenceList}>
                  {insight.evidence.map((ev, i) => (
                    <li key={i}>
                      <EvidenceItem
                        text={ev}
                        labelClass={styles.modalEvidenceLabel}
                        valueClass={styles.modalEvidenceValue}
                        highlightClass={styles.highlight}
                      />
                    </li>
                  ))}
                </ul>
              )}
              {insight.isExtendedRange && insight.rangeWarning && (
                <div className={styles.modalRangeWarning}>
                  {renderHighlights(insight.rangeWarning, styles.highlight)}
                </div>
              )}
              {insight.periodLabel ? (
                <p className={styles.modalPeriod}>
                  <span className={styles.modalPeriodLabel}>Período</span>
                  <span>{insight.periodLabel}</span>
                </p>
              ) : null}
              {insight.id && (
                <div className={styles.modalRatingRow}>
                  <span className={styles.ratingLabel}>Sua avaliação</span>
                  <RatingStars
                    value={rating}
                    hoverValue={hoverRating}
                    onChange={handleRate}
                    onHover={setHoverRating}
                    disabled={ratingState === "saving"}
                  />
                  {ratingState === "saving" && (
                    <span className={styles.ratingFeedback}>Salvando...</span>
                  )}
                  {ratingState === "saved" && rating > 0 && (
                    <span className={styles.ratingFeedback}>Obrigado pelo feedback!</span>
                  )}
                  {ratingState === "error" && (
                    <span className={`${styles.ratingFeedback} ${styles.ratingFeedback_error}`}>
                      {ratingError}
                    </span>
                  )}
                </div>
              )}
            </div>
          </div>,
          document.body
        )}
    </div>
  );
};

const INITIAL_INSIGHTS_LIMIT = 4;

const AiInsightsBlock = ({ institutionId, isConsolidatedView }) => {
  const [snapshot, setSnapshot] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [regenerating, setRegenerating] = useState(false);
  const [statusMessage, setStatusMessage] = useState(null);
  const [showAll, setShowAll] = useState(false);
  const [orderedInsights, setOrderedInsights] = useState([]);
  const [orderSaveMsg, setOrderSaveMsg] = useState(null);
  const saveOrderTimerRef = useRef(null);

  const loadSnapshot = useCallback(async () => {
    if (!institutionId || isConsolidatedView) return;
    setLoading(true);
    setError(null);
    try {
      const data = await getAiInsights({ institutionId });
      setSnapshot(data);
      setShowAll(false);
      if (data?.snapshotId) {
        recordAiInsightInteraction({
          institutionId,
          snapshotId: data.snapshotId,
          seen: true,
        });
      }
    } catch (err) {
      setError(err?.message || "Falha ao buscar Insights com IA.");
      setSnapshot(null);
    } finally {
      setLoading(false);
    }
  }, [institutionId, isConsolidatedView]);

  useEffect(() => {
    loadSnapshot();
  }, [loadSnapshot]);

  useEffect(() => {
    setOrderedInsights([]);
  }, [institutionId]);

  useEffect(() => {
    if (snapshot?.insights?.length) {
      setOrderedInsights([...snapshot.insights]);
    } else if (!snapshot) {
      setOrderedInsights([]);
    }
  }, [snapshot?.snapshotId]);

  const persistDisplayOrder = useCallback(
    (types) => {
      if (!institutionId || !types?.length) return;
      if (saveOrderTimerRef.current) clearTimeout(saveOrderTimerRef.current);
      setOrderSaveMsg("Salvando ordem...");
      saveOrderTimerRef.current = setTimeout(async () => {
        saveOrderTimerRef.current = null;
        try {
          await saveAiInsightDisplayOrder({ institutionId, orderedTypes: types });
          setOrderSaveMsg("Prioridade salva.");
          setTimeout(() => {
            setOrderSaveMsg((m) => (m === "Prioridade salva." ? null : m));
          }, 2400);
        } catch (e) {
          setOrderSaveMsg(e?.message || "Falha ao salvar ordem.");
        }
      }, 450);
    },
    [institutionId]
  );

  useEffect(
    () => () => {
      if (saveOrderTimerRef.current) clearTimeout(saveOrderTimerRef.current);
    },
    []
  );

  const handleRegenerate = useCallback(async () => {
    if (!institutionId) return;
    setRegenerating(true);
    setStatusMessage(null);
    try {
      await regenerateAiInsights({ institutionId });
      setStatusMessage(
        "Geração enfileirada. Os novos insights aparecem em alguns instantes — atualize a página para ver."
      );
    } catch (err) {
      setStatusMessage(err?.message || "Falha ao regenerar.");
    } finally {
      setRegenerating(false);
    }
  }, [institutionId]);

  const insights = orderedInsights.length ? orderedInsights : snapshot?.insights || [];

  const moveHeadInsight = useCallback(
    (fromIndex, toIndex) => {
      if (fromIndex === toIndex) return;
      setOrderedInsights((prev) => {
        if (!prev.length) return prev;
        const len = showAll
          ? prev.length
          : Math.min(INITIAL_INSIGHTS_LIMIT, prev.length);
        if (
          fromIndex < 0 ||
          toIndex < 0 ||
          fromIndex >= len ||
          toIndex >= len
        ) {
          return prev;
        }
        const head = prev.slice(0, len);
        const tail = prev.slice(len);
        const [moved] = head.splice(fromIndex, 1);
        head.splice(toIndex, 0, moved);
        const next = [...head, ...tail];
        persistDisplayOrder(next.map((i) => i.type));
        return next;
      });
    },
    [showAll, persistDisplayOrder]
  );

  const hasMoreInsights = insights.length > INITIAL_INSIGHTS_LIMIT;
  const visibleInsights = showAll ? insights : insights.slice(0, INITIAL_INSIGHTS_LIMIT);

  if (isConsolidatedView) {
    return (
      <div className={styles.aiTabContainer}>
        <div className={styles.block}>
          <div className={styles.header}>
            <div className={styles.headerLeft}>
              <h3 className={styles.title}>
                Insights com IA <span className={styles.aiBadge}>IA</span>
              </h3>
            </div>
          </div>
          <div className={styles.empty}>
            Selecione uma instituição para gerar insights com IA.
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.aiTabContainer}>
      <div className={styles.block}>
      <div className={styles.header}>
        <div className={styles.headerLeft}>
          <h3 className={styles.title}>
            Insights com IA <span className={styles.aiBadge}>IA</span>
          </h3>
          {snapshot?.completedAt && (
            <p className={styles.subtitle}>
              Gerado em {formatDateTime(snapshot.completedAt)}
            </p>
          )}
        </div>
        <div className={styles.headerRight}>
          <button
            type="button"
            className={styles.regenerateBtn}
            onClick={handleRegenerate}
            disabled={regenerating || loading}
          >
            {regenerating ? "Enfileirando..." : "Regerar"}
          </button>
        </div>
      </div>

      {statusMessage && (
        <div className={styles.warning}>
          <span className={styles.warningIcon}>i</span>
          <span>{statusMessage}</span>
        </div>
      )}

      {snapshot && (
        <div className={styles.metaRow}>
          <span>
            Período analisado: {formatDate(snapshot.effectiveStartDate)} – {formatDate(snapshot.effectiveEndDate)}
            {snapshot.periodMonths ? ` (${snapshot.periodMonths} meses)` : ""}
          </span>
        </div>
      )}

      {snapshot?.isExtendedRange && snapshot?.extendedRangeReason && (
        <div className={styles.warning}>
          <span className={styles.warningIcon}>!</span>
          <span>
            <strong>Janela ampliada:</strong> {snapshot.extendedRangeReason}
          </span>
        </div>
      )}

      {loading && <div className={styles.loading}>Carregando insights...</div>}
      {error && !loading && <div className={styles.error}>{error}</div>}

      {!loading && !error && !snapshot && (
        <div className={styles.empty}>
          Ainda não há insights gerados para esta instituição. Use o botão "Regerar" para gerar agora.
        </div>
      )}

      {!loading && !error && snapshot && insights.length === 0 && (
        <div className={styles.empty}>
          O último processamento não retornou insights válidos. Tente regenerar.
        </div>
      )}

      {!loading && !error && insights.length > 0 && (
        <>
          {insights.length >= 2 && (
            <div className={styles.priorityRow}>
              <p className={styles.priorityHint}>
                Use a alça à esquerda de cada card para ordenar do mais prioritário ao menos
                prioritário. Essa ordem é salva para a instituição e usada nas próximas gerações
                de insights (incluindo o processamento diário).
              </p>
              {orderSaveMsg ? (
                <p
                  className={`${styles.orderSaveMsg} ${
                    orderSaveMsg.startsWith("Falha") ? styles.orderSaveMsg_error : ""
                  }`}
                >
                  {orderSaveMsg}
                </p>
              ) : null}
            </div>
          )}
          <div className={styles.cards}>
            {visibleInsights.map((insight, idx) => {
              const canReorder = insights.length >= 2;
              return (
                <div
                  key={insight.id || `${insight.type}-${idx}`}
                  className={styles.cardDragRow}
                  onDragOver={
                    canReorder
                      ? (e) => {
                          e.preventDefault();
                          e.dataTransfer.dropEffect = "move";
                        }
                      : undefined
                  }
                  onDrop={
                    canReorder
                      ? (e) => {
                          e.preventDefault();
                          const raw = e.dataTransfer.getData("text/plain");
                          const from = parseInt(raw, 10);
                          if (!Number.isNaN(from)) {
                            moveHeadInsight(from, idx);
                          }
                        }
                      : undefined
                  }
                >
                  {canReorder ? (
                    <button
                      type="button"
                      className={styles.dragHandle}
                      draggable
                      onDragStart={(e) => {
                        e.dataTransfer.setData("text/plain", String(idx));
                        e.dataTransfer.effectAllowed = "move";
                      }}
                      aria-label="Arrastar para alterar prioridade do insight"
                      title="Arrastar para prioridade"
                    >
                      ⋮⋮
                    </button>
                  ) : null}
                  <div className={styles.cardDragRowInner}>
                    <InsightCard
                      insight={insight}
                      snapshotId={snapshot.snapshotId}
                      institutionId={institutionId}
                      initialRating={
                        insight.id && snapshot.myRatings
                          ? snapshot.myRatings[insight.id] || 0
                          : 0
                      }
                    />
                  </div>
                </div>
              );
            })}
          </div>
          {hasMoreInsights && (
            <div className={styles.showMoreRow}>
              <button
                type="button"
                className={styles.showMoreBtn}
                onClick={() => setShowAll((prev) => !prev)}
              >
                {showAll
                  ? "Ver menos"
                  : `Ver mais (${insights.length - INITIAL_INSIGHTS_LIMIT})`}
              </button>
            </div>
          )}
        </>
      )}
      </div>

      <div className={styles.sectionDivider}>
        <h3 className={styles.sectionTitle}>Dados da sua instituição</h3>
        <p className={styles.sectionDescription}>
          Nesta seção você vê os principais indicadores da sua instituição,
          complementados pelos insights gerados com IA acima.
        </p>
      </div>

      <InstitutionRevenueHistoryCard institutionId={institutionId} />
      <InstitutionBillingHistoryCard institutionId={institutionId} />
      <InstitutionAverageTicketHistoryCard institutionId={institutionId} />
    </div>
  );
};

export default AiInsightsBlock;
